import CreationToolCutOutMode from 'creation/src/creation-tools/types/cut-out-mode'
import type { ElementType } from 'react'
import { EraseIcon, LassoIcon, MagicWandIcon, PaintIcon } from '@/app/icons/cut-out'

/**
 * The two halves of legacy's types/cut-out-mode.ts that cannot live in the
 * shared package: `icon()` because icon components need React, and `color()`
 * because the port wants Tailwind utilities rather than the hex values
 * `@giphy/colors` supplies. Same split Task 3 of Foundation applied to
 * `welcomeButtonIcon`.
 *
 * Legacy uses one hex value in three CSS positions, so this exports two class
 * helpers: a background and a fill. Both must be complete literal class names
 * — Tailwind's scanner cannot resolve `bg-${colour}`.
 */
const icons: Record<CreationToolCutOutMode, ElementType> = {
    [CreationToolCutOutMode.Lasso]: LassoIcon,
    [CreationToolCutOutMode.Paint]: PaintIcon,
    [CreationToolCutOutMode.MagicWand]: MagicWandIcon,
    [CreationToolCutOutMode.Erase]: EraseIcon,
}

// Legacy: types/cut-out-mode.ts:31-42 — giphyPurple / giphyGreen / giphyBlue / giphyPink.
const backgrounds: Record<CreationToolCutOutMode, string> = {
    [CreationToolCutOutMode.Lasso]: 'bg-giphyPurple',
    [CreationToolCutOutMode.Paint]: 'bg-giphyGreen',
    [CreationToolCutOutMode.MagicWand]: 'bg-giphyBlue',
    [CreationToolCutOutMode.Erase]: 'bg-giphyPink',
}

// Legacy's rule is `svg path[fill] { fill: … }` (cut-out/button-icon.tsx:33-35):
// it selects descendant `<path>` elements directly, because a bare `fill-*`
// on the `<svg>` itself loses to the path's own `fill="#hex"` presentation
// attribute — a presentation attribute counts as a specified value on that
// element, so it wins over anything merely inherited from an ancestor.
// The `[&_path[fill]]:` variant has to be baked into these literals rather
// than composed at the call site, because Tailwind's scanner can't resolve
// an interpolated compound like `` `[&_path[fill]]:${fill}` `` — it needs the
// whole class to appear as one unbroken literal.
const fills: Record<CreationToolCutOutMode, string> = {
    [CreationToolCutOutMode.Lasso]: '[&_path[fill]]:fill-giphyPurple',
    [CreationToolCutOutMode.Paint]: '[&_path[fill]]:fill-giphyGreen',
    [CreationToolCutOutMode.MagicWand]: '[&_path[fill]]:fill-giphyBlue',
    [CreationToolCutOutMode.Erase]: '[&_path[fill]]:fill-giphyPink',
}

const hovers: Record<CreationToolCutOutMode, string> = {
    [CreationToolCutOutMode.Lasso]: 'hover:bg-giphyPurple',
    [CreationToolCutOutMode.Paint]: 'hover:bg-giphyGreen',
    [CreationToolCutOutMode.MagicWand]: 'hover:bg-giphyBlue',
    [CreationToolCutOutMode.Erase]: 'hover:bg-giphyPink',
}

export const cutOutModeIcon = (mode: CreationToolCutOutMode): ElementType => icons[mode]
export const cutOutModeColor = (mode: CreationToolCutOutMode): string => backgrounds[mode]
export const cutOutModeFill = (mode: CreationToolCutOutMode): string => fills[mode]
export const cutOutModeHoverColor = (mode: CreationToolCutOutMode): string => hovers[mode]
